import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiFetch, ApiClientError } from '@/lib/api-client'
import { Logo } from '@/components/Logo'

interface BrokerLoginResponse {
  accessToken: string
  broker: {
    id: string
    name: string
    email: string
    company: string | null
    tier: string
    territory: string
    artistId: string
    parentId: string | null
  }
}

/**
 * Standalone login page for brokers. Posts to /broker-portal/auth/login
 * and stores the broker JWT in localStorage separately from the main auth store.
 */
export function BrokerLoginPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const data = await apiFetch<BrokerLoginResponse>('/broker-portal/auth/login', {
        method: 'POST',
        body: { email, password },
        skipAuth: true,
      })
      // Store broker session separately from the main app auth
      localStorage.setItem('ovation.broker.token', data.accessToken)
      localStorage.setItem('ovation.broker.profile', JSON.stringify(data.broker))
      navigate('/broker-portal', { replace: true })
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message)
      } else {
        setError('Unexpected error. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex h-full items-center justify-center bg-gradient-to-b from-ovation-paper to-white px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>
        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-xl border border-ovation-ink/10 bg-white p-7 shadow-xl shadow-ovation-ink/5"
        >
          <div>
            <h1 className="font-display text-2xl tracking-tight">Broker Portal</h1>
            <p className="mt-1 text-sm text-ovation-muted">
              Sign in to view your pipeline and activity.
            </p>
          </div>

          <div>
            <label htmlFor="broker-email" className="mb-1 block text-sm font-medium">
              Email
            </label>
            <input
              id="broker-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
            />
          </div>

          <div>
            <label htmlFor="broker-password" className="mb-1 block text-sm font-medium">
              Password
            </label>
            <input
              id="broker-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
            />
          </div>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper transition-colors hover:bg-ovation-ink/90 disabled:opacity-50"
          >
            {submitting ? 'Signing in...' : 'Sign in'}
          </button>

          <div className="border-t border-ovation-ink/10 pt-4 text-center text-xs text-ovation-muted">
            Contact your artist management team if you need access.
          </div>
        </form>

        <p className="mt-6 text-center text-xs text-ovation-muted">
          The preparation is ours. The ovation is yours.
        </p>
        <p className="mt-2 text-center text-[9px] uppercase tracking-[0.18em] text-ovation-muted/70">
          <a
            href="https://geekybee.net"
            target="_blank"
            rel="noreferrer"
            className="hover:text-ovation-ink"
          >
            A Geekybee.net product
          </a>
        </p>
      </div>
    </div>
  )
}
